import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import RedditLogo from '@/assets/icons/reddit-logo.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { getOptimalFontSizeSimple } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    // Captures natural browser line breaks
    const getNaturalLineBreaks = (
      text: string,
      fontSize: number,
      maxWidth: number,
    ): string => {
      const tester = document.createElement('p');
      tester.style.position = 'absolute';
      tester.style.left = '-9999px';
      tester.style.top = '-9999px';
      tester.style.width = `${maxWidth}px`;
      tester.style.fontFamily = 'Arial, Helvetica, sans-serif';
      tester.style.fontSize = `${fontSize}px`;
      tester.style.lineHeight = '1.2';
      tester.style.fontWeight = '400';
      tester.style.whiteSpace = 'normal';
      tester.style.wordWrap = 'break-word';
      tester.textContent = text;

      document.body.appendChild(tester);

      try {
        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = '';
        let testLine = '';

        for (let i = 0; i < words.length; i++) {
          testLine = currentLine ? `${currentLine} ${words[i]}` : words[i];
          tester.textContent = testLine;

          // Check if adding this word exceeds one line
          const lineCount = Math.round(
            tester.scrollHeight / (fontSize * 1.2),
          );

          if (lineCount > 1 && currentLine) {
            lines.push(currentLine);
            currentLine = words[i];
          } else {
            currentLine = testLine;
          }
        }

        if (currentLine) {
          lines.push(currentLine);
        }

        return lines.join('<br/>');
      } finally {
        document.body.removeChild(tester);
      }
    };

    const generateImageVariations = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder');
            imageHolder.style.backgroundColor = variation.coverColor;

            const innerDiv = document.createElement('div');
            innerDiv.classList.add('inner-div');

            const channelRow = document.createElement('div');
            channelRow.classList.add('channel-row');

            const channelImage = new Image();
            channelImage.classList.add('channel-image');

            if (variation.authorImage) {
              channelImage.src = variation.authorImage;
            } else {
              channelImage.src = RedditLogo;
            }

            const channelName = document.createElement('p');
            channelName.innerText = variation.authorName;
            channelName.classList.add('channel-name');

            if (variation.channelNameColor === 'blue') {
              channelName.classList.add('channel-name__blue');
            }

            const textP = document.createElement('p');
            textP.classList.add('text');
            const cleanText = replaceQuotes(variation.coverText);

            let fontSize: number;
            if (variation.fontSize) {
              fontSize = variation.fontSize;
            } else {
              fontSize = getOptimalFontSizeSimple(
                cleanText,
                960,
                variation.channelImageOption === 'none' ? 440 : 340,
                'Arial',
              );
            }

            textP.style.fontSize = `${fontSize}px`;
            textP.innerHTML = getNaturalLineBreaks(cleanText, fontSize, 960);

            channelRow.appendChild(channelImage);
            channelRow.appendChild(channelName);

            if (variation.channelImageOption !== 'none') {
              innerDiv.appendChild(channelRow);
            }

            innerDiv.appendChild(textP);

            imageHolder.appendChild(innerDiv);

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariations,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
